import { motion } from "framer-motion";
import { Lightbulb, Camera, Scissors, MonitorPlay, Check } from "lucide-react";

const STAGES = [
  {
    n: "01", icon: Lightbulb, phase: "PRE-PRODUCTION", title: "Think.",
    desc: "Turn a raw idea into a production-ready plan.",
    items: ["Story & scripting", "Shot breakdowns", "Budgeting", "Production design"],
  },
  {
    n: "02", icon: Camera, phase: "PRODUCTION", title: "Shoot.",
    desc: "Build confidence with professional cameras, light and sound.",
    items: ["Camera operation", "Lighting setups", "Location sound", "Directing talent"],
  },
  {
    n: "03", icon: Scissors, phase: "POST-PRODUCTION", title: "Shape.",
    desc: "Find the story in the edit and finish it for every screen.",
    items: ["Premiere Pro", "After Effects", "Sound design", "Colour grading"],
  },
  {
    n: "04", icon: MonitorPlay, phase: "PROFESSIONAL WORKFLOW", title: "Deliver.",
    desc: "Work like a studio — from feedback and exports to the final handoff.",
    items: ["Client workflow", "File management", "Showreel building", "Freelance basics"],
  },
];

const MILESTONES = ["IDEA", "PRE-PRODUCTION", "PRODUCTION", "POST", "DELIVERY"];

export default function PipelineSection() {
  return (
    <section id="pipeline" className="bg-lima-ink px-5 md:px-10 py-14 md:py-32 border-b border-white/10" data-testid="pipeline-section">
      <div className="flex items-start justify-between mb-10 md:mb-14">
        <div>
          <span className="inline-block font-mono2 text-xs tracking-[0.25em] text-white bg-lima-blue px-2.5 py-1">
            FROM FIRST IDEA TO FINAL DELIVERY
          </span>
        </div>
        <p className="hidden md:block max-w-[260px] text-right text-sm text-white/40 leading-relaxed pt-1">
          One connected learning path. Four professional stages. Every lesson feeds the next.
        </p>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 40, scale: 1.03, filter: "blur(16px)" }}
        whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-lima-paper leading-[1.02] text-center mb-10 md:mb-20"
      >
        Learn the whole
        <br />
        <span className="font-serifit font-normal text-lima-blue">production pipeline.</span>
      </motion.h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-white/10">
        {STAGES.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className={`flex flex-col p-7 md:p-9 lg:min-h-[380px] border-white/10 ${i > 0 ? "border-t sm:border-t lg:border-t-0 lg:border-l" : ""} ${i === 2 ? "sm:border-t lg:border-t-0" : ""} ${i % 2 === 1 ? "sm:border-l lg:border-l" : ""} hover:bg-white/[0.02] transition-colors duration-300`}
            data-testid={`pipeline-stage-${s.n}`}
          >
            <div className="flex items-start justify-between mb-10">
              <span className="font-mono2 text-xs text-white/40">{s.n}</span>
              <s.icon size={18} className="text-white/50" />
            </div>
            <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-2">{s.phase}</p>
            <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-lima-paper mb-8">
              {s.title.slice(0, -1)}
              <span className="text-lima-blue">.</span>
            </h3>
            <p className="text-sm text-white/50 leading-relaxed underline underline-offset-4 decoration-white/20 mb-6">{s.desc}</p>
            <ul className="mt-auto space-y-2.5">
              {s.items.map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-white/70">
                  <Check size={13} className="text-lima-blue shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between" data-testid="pipeline-milestones">
        {MILESTONES.map((m, i) => (
          <span key={m} className="flex items-center gap-3 font-mono2 text-[10px] md:text-[11px] tracking-[0.2em] text-white/40">
            <span className={`w-1.5 h-1.5 ${i === 0 ? "bg-white/40" : "bg-lima-blue"}`} aria-hidden="true" />
            <span className={i === 0 ? "hidden sm:inline" : ""}>{m}</span>
          </span>
        ))}
      </div>
    </section>
  );
}
